import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Briefcase, Code2, Rocket, ArrowRight } from 'lucide-react';

export const ProcessSectionHTAG = () => {
  const steps = [
    {
      number: '01',
      title: 'Cadrage & Audit',
      icon: Briefcase,
      description: 'Nous commençons par comprendre votre contexte, vos objectifs, vos contraintes et votre audience. Audit complet et recommandations.',
      color: 'from-blue-500/20 to-blue-600/20',
      borderColor: 'border-blue-500/30'
    },
    {
      number: '02',
      title: 'Design & Prototype',
      icon: CheckCircle,
      description: 'Création de maquettes UX/UI, tests utilisateur rapides, itérations basées sur vos retours. Approbation finale avant développement.',
      color: 'from-purple-500/20 to-purple-600/20',
      borderColor: 'border-purple-500/30'
    },
    {
      number: '03',
      title: 'Développement & QA',
      icon: Code2,
      description: 'Développement itératif avec démos hebdos, tests exhaustifs (performance, accessibilité, SEO), intégration des retours en continu.',
      color: 'from-cyan-500/20 to-cyan-600/20',
      borderColor: 'border-cyan-500/30'
    },
    {
      number: '04',
      title: 'Lancement & Suivi',
      icon: Rocket,
      description: 'Déploiement en production, monitoring 24/7, documentation complète, formation équipe, suivi analytique et optimisations continues.',
      color: 'from-orange-500/20 to-orange-600/20',
      borderColor: 'border-orange-500/30'
    }
  ];

  return (
    <section className="py-24 px-4 lg:px-8 bg-gradient-to-b from-[#212635] to-[#181C25]">
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl lg:text-5xl font-montserrat font-bold text-white mb-6">
            Notre <span style={{ color: '#0090FF', textShadow: '0 0 20px rgba(0, 144, 255, 0.3)' }}>Processus</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Une approche structurée et transparente, du cadrage à la livraison et au suivi.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Ligne de connexion (desktop) */}
          <div className="hidden lg:block absolute top-40 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-orange-500/20" />

          {/* Étapes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.6 }}
                  className="relative"
                >
                  {/* Card */}
                  <div className={`relative z-10 p-8 rounded-xl border ${step.borderColor} bg-gradient-to-br ${step.color} hover:border-opacity-100 transition-all duration-300 h-full`}>
                    {/* Numéro */}
                    <div className="text-5xl font-montserrat font-bold text-gray-600 opacity-30 mb-4">
                      {step.number}
                    </div>

                    {/* Icône */}
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 p-2.5 mb-6 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>

                    {/* Titre */}
                    <h3 className="text-xl font-montserrat font-bold text-white mb-3">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Flèche de connexion (desktop) */}
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-40 -right-4 z-0">
                      <ArrowRight className="w-8 h-8 text-gray-600 opacity-40" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-20"
        >
          <p className="text-gray-300 text-lg mb-8">
            Prêt à transformer votre projet en réalité ?
          </p>
          <a
            href="/?scroll=contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all hover:scale-105"
          >
            Parler de votre projet
            <ArrowRight size={20} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSectionHTAG;
